// Turns a raw diagnostic trouble code into something a car owner can act on.
//
// A direct mirror of the Flutter app's lib/services/dtc_catalog.dart. The two
// must stay in step: the same car opened on the phone and on the web has to be
// told the same thing about the same fault.
//
// The server's dtc_codes catalogue is generated at runtime by a language model
// and has no human review and no severity, so it is not a safe basis for telling
// someone whether to keep driving. This map is hand written, small, and
// reviewable in one sitting. It covers the codes that actually turn up: Illinois
// EPA emissions data across ~1.67M vehicles puts EVAP at 24% of check-engine
// failures, misfire 17%, lean fuel trim 17%, catalyst 16%.
//
// Deliberately NOT derived from the lamp. A flashing check-engine light is the
// real "stop driving" signal, but it is a dashboard behaviour the ECU drives
// locally - OBD-II mode 01 PID 01 carries a single on/off bit and no scan tool
// can read the flash. So severity comes from the codes, and where a flash would
// have mattered we ask the driver to look at their own dashboard instead.
//
// Deliberately free of imports so it can be unit-tested under `node --test`.

// Ordered least to most severe; compare with SEVERITY_RANK.
export const DtcSeverity = {
    allGood: 'allGood',
    watch: 'watch',
    attention: 'attention',
    urgent: 'urgent',
};

export const SEVERITY_RANK = {
    allGood: 0,
    watch: 1,
    attention: 2,
    urgent: 3,
};

// Never assert that a car is safe. We read what the computer reported; we cannot
// inspect tyres, brakes, fluids, or anything the ECU does not see.
export const SCOPE_DISCLAIMER =
    'SimpliGPS reads the codes your car reports. We are not a mechanic and '
    + 'cannot inspect your vehicle. Confirm with a qualified technician before '
    + 'paying for any repair.';

// Shown for anything urgent. This is how we honestly stand in for the flashing
// lamp we cannot read.
export const BLINKING_LAMP_WARNING =
    'If the light on your dashboard is blinking, or the car is shaking or '
    + 'losing power, pull over somewhere safe and call for help.';

const CATALYST_ACTION =
    'Fine to keep driving. Book a shop visit in the next couple of weeks - '
    + 'this one can be expensive, so it is worth a second quote.';
const EVAP_ACTION =
    'Tighten your fuel cap until it clicks, then drive normally for a few '
    + 'days. If the light stays on, book a shop visit.';
const MISFIRE_ACTION =
    'Drive gently and get it looked at today. Misfires can damage the '
    + 'catalytic converter, which is a costly repair.';

const CODES = {
    // Catalyst - 16% of check-engine failures.
    P0420: ["Your catalytic converter isn't cleaning the exhaust as well as it should", CATALYST_ACTION, 'attention'],
    P0430: ['The catalytic converter on the other side of the engine is underperforming', CATALYST_ACTION, 'attention'],

    // Fuel trim - 17%.
    P0171: ['The engine is getting more air than fuel, often a small air leak or a dirty sensor',
        'Safe to drive normally. Get it checked within a week or two - left alone it can damage the converter.', 'attention'],
    P0174: ['The same air-to-fuel imbalance, on the other side of the engine',
        'Safe to drive normally. Get it checked within a week or two.', 'attention'],
    P0172: ['The engine is burning more fuel than it should',
        "Safe to drive. You'll waste fuel until it's fixed, so book a shop visit.", 'attention'],
    P0175: ['Burning too much fuel, on the other side of the engine', 'Safe to drive. Book a shop visit.', 'attention'],

    // EVAP - 24%, and the most common cause is a loose fuel cap.
    P0442: ['A small fuel-vapour leak, very often just a loose fuel cap', EVAP_ACTION, 'watch'],
    P0455: ['A large fuel-vapour leak, usually a loose, cracked or missing fuel cap',
        'Check your fuel cap first and replace it if the seal looks cracked. A few days of normal driving should clear it.', 'watch'],
    P0456: ['A very small fuel-vapour leak, most often the fuel cap', EVAP_ACTION, 'watch'],
    P0457: ['Your fuel cap is loose or missing', 'Tighten or replace the fuel cap. No mechanic needed.', 'watch'],
    P0440: ['A fault in the system that stops fuel fumes escaping', EVAP_ACTION, 'watch'],
    P0441: ['The fuel-vapour system is not purging correctly', EVAP_ACTION, 'watch'],
    P0446: ['A fault in the fuel-vapour venting system', EVAP_ACTION, 'watch'],
    P0411: ["An emissions pump isn't flowing correctly",
        "Doesn't affect how the car drives. Mention it at your next service.", 'watch'],

    // EGR.
    P0401: ['The exhaust-recirculation valve is clogged or stuck',
        'Safe to drive, though you may notice rough idling. Often just needs cleaning, so book a shop visit.', 'attention'],
    P0402: ['Too much exhaust is being recirculated', 'Safe to drive. Book a shop visit.', 'attention'],

    // Thermostat and sensors.
    P0128: ["The engine isn't warming up properly, usually a stuck thermostat",
        "Safe to drive. You'll use more fuel and get weak heat. Usually a cheap fix, so book a shop visit.", 'attention'],
    P0101: ['The sensor measuring incoming air is misreading',
        'Safe to drive but it may hesitate. Sometimes fixed by cleaning the sensor, so book a shop visit.', 'attention'],
    P0113: ['An air-temperature sensor is reading out of range',
        'Safe to drive. Mention it at your next service.', 'watch'],
    P0117: ['The engine-temperature sensor is misreading',
        'Watch your temperature gauge. If it reads hot, pull over safely. Otherwise book a shop visit.', 'attention'],
    P0325: ["The sensor that listens for engine knock isn't reporting",
        'Safe to drive, but avoid hard acceleration. Book a shop visit.', 'attention'],

    // Urgent: drivability, safety, or compounding damage.
    P0300: ['The engine is stumbling on more than one cylinder', MISFIRE_ACTION, 'urgent'],
    P0011: ["The engine's valve timing is off, often from low or dirty oil",
        'Check your oil level. Book a shop visit soon - timing faults get expensive if ignored.', 'attention'],
    P0016: ['Two engine-timing sensors disagree with each other',
        'Get this checked before driving far. Timing problems can cause serious engine damage.', 'urgent'],
    P0121: ["The sensor tracking your accelerator isn't reporting properly",
        'The car may hesitate or drop into reduced power. Drive straight to a mechanic, or not at all if it feels unpredictable.', 'urgent'],
    P0217: ['The engine has run too hot',
        'Pull over safely when you can and let it cool. Do not keep driving an overheating engine.', 'urgent'],
    P0335: ['A core engine sensor is failing',
        "The car may stall or refuse to restart. Get it to a mechanic now - don't start a long trip.", 'urgent'],
    P0340: ['A core engine sensor is failing', 'The car may stall or not restart. Get it to a mechanic now.', 'urgent'],
    P0562: ["The car's charging system voltage is low",
        'Often a failing alternator or battery. You may not restart after switching off. Get it checked today.', 'urgent'],
    P0563: ["The car's charging system voltage is high",
        'Get it checked today - high voltage can damage the electronics.', 'urgent'],

    // Transmission.
    P0700: ['The transmission computer has reported a fault',
        'This is a pointer code - the real detail is a second transmission code. Book a shop visit.', 'attention'],
    P0740: ["A transmission component isn't engaging properly",
        'You may feel shuddering. Book a shop visit soon - transmission repairs get worse with delay.', 'attention'],

    // Idle and speed.
    P0505: ['The engine idles too high, too low, or stalls at stops',
        'Safe to drive, but it may stall at lights. Book a shop visit soon.', 'attention'],
    P0500: ["The car's speed sensor isn't reporting properly",
        'Cruise control and the speedometer may misbehave. Book a shop visit.', 'attention'],
};

const inRange = (code, low, high) => code >= low && code <= high;

// A manufacturer-specific code we have no entry for.
//
// Returns a useful handoff rather than a shrug: this is one of the few places
// where showing the raw code prominently is the right call, because the dealer
// can look it up.
const unknown = (code) => ({
    plain: 'Your car reported a code we do not have details for',
    action: `Give your dealer or mechanic this code and they can look it up: ${code}`,
    severity: 'attention',
    known: false,
});

/**
 * Look up a code, falling back through families to a safe default.
 *
 * An unrecognised code is `attention`, never allGood and never urgent. Green
 * would be a lie and red would be crying wolf; amber with an honest "we don't
 * have details on this one" is the defensible position.
 */
export function lookupDtc(rawCode) {
    const code = String(rawCode ?? '').trim().toUpperCase();
    const exact = CODES[code];
    if (exact) return {plain: exact[0], action: exact[1], severity: exact[2], known: true};

    if (code.length < 5) return unknown(code);

    // Oxygen sensors: P0130-P0167 heater/circuit/response faults.
    if (inRange(code, 'P0130', 'P0167')) {
        return {
            plain: 'An oxygen sensor that helps tune the engine is not reporting properly',
            action: 'Safe to drive. Expect worse fuel economy. A common, moderate-cost fix.',
            severity: 'attention', known: true,
        };
    }
    // Single-cylinder misfires P0301-P0312.
    if (inRange(code, 'P0301', 'P0312')) {
        return {plain: "One cylinder isn't firing properly", action: MISFIRE_ACTION, severity: 'urgent', known: true};
    }
    // Ignition coils P0351-P0362 - these usually cause a misfire too.
    if (inRange(code, 'P0351', 'P0362')) {
        return {plain: 'An ignition coil is failing', action: MISFIRE_ACTION, severity: 'urgent', known: true};
    }
    // Remaining EVAP P0443-P0459.
    if (inRange(code, 'P0443', 'P0459')) {
        return {
            plain: 'A fault in the system that stops fuel fumes escaping',
            action: EVAP_ACTION, severity: 'watch', known: true,
        };
    }

    switch (code[0]) {
        case 'U':
            // Network faults: the car's computers have stopped talking.
            return {
                plain: "Two of the car's computers have stopped talking to each other",
                action: 'The car may behave unpredictably. Get it to a mechanic before driving further.',
                severity: 'urgent', known: true,
            };
        case 'C':
            // Chassis: ABS, traction, stability. Never framed as "engine".
            return {
                plain: 'A braking or stability system has reported a fault',
                action: 'Your normal brakes usually still work, but anti-lock and stability assist may not. Drive carefully and get it checked.',
                severity: 'urgent', known: true,
            };
        case 'B':
            // Body: airbags and restraints. Must never sit in an amber "engine" card.
            return {
                plain: 'A safety-restraint system has reported a fault',
                action: 'Airbags may not deploy in a crash. Get this checked before driving with passengers.',
                severity: 'urgent', known: true,
            };
        default:
            return unknown(code);
    }
}

/**
 * The single state the card should present.
 *
 * `stored` and `permanent` drive it. Pending codes deliberately do NOT: a
 * pending code means the car saw something once and is not yet convinced, and
 * treating that as a fault manufactures anxiety and support load.
 */
export function overallSeverity({milOn = false, stored = [], permanent = []} = {}) {
    const codes = [...(stored || []), ...(permanent || [])];
    if (!codes.length) return milOn ? DtcSeverity.attention : DtcSeverity.allGood;

    let worst = DtcSeverity.watch;
    for (const code of codes) {
        const s = lookupDtc(code).severity;
        if (SEVERITY_RANK[s] > SEVERITY_RANK[worst]) worst = s;
    }
    return worst;
}

/**
 * The highest-severity code, which is the only one a compact card shows.
 * "3 stored, 1 pending" means nothing to a car owner and reads as alarming.
 */
export function leadCode(stored = [], permanent = []) {
    const codes = [...(stored || []), ...(permanent || [])];
    if (!codes.length) return null;

    return [...codes].sort(
        (a, b) => SEVERITY_RANK[lookupDtc(b).severity] - SEVERITY_RANK[lookupDtc(a).severity],
    )[0];
}

/** The one line at the top. Says what to DO, not how many codes there are. */
export function severityHeadline(severity) {
    switch (severity) {
        case DtcSeverity.allGood: return 'No faults reported';
        case DtcSeverity.watch: return 'Something to keep an eye on';
        case DtcSeverity.urgent: return 'Needs attention now';
        default: return 'Worth getting checked';
    }
}

/** Amber is the resting state; green only when the car really did report clean. */
export function severityColor(severity) {
    switch (severity) {
        // Matches AppDesign.live in the Flutter app, so the clean state is the
        // same green on both.
        case DtcSeverity.allGood: return '#22C55E';
        case DtcSeverity.urgent: return '#d70000';
        default: return '#F97316';
    }
}
